import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/reports")({
  component: InventoryReportsPage,
});

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";
import type { DateRange } from "react-day-picker";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateSelector } from "@/components/crud/filters/DateSelector";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useInventoryReports,
  type MovementReportParams,
  type AdjustmentReportParams,
} from "@/hooks/api/inventory/useReports";
import {
  useFetchWarehouses,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { StockLevelTable } from "@/components/inventory/tables/StockLevelTable";
import { TransactionTable } from "@/components/inventory/tables/TransactionTable";
import { AdjustmentReportResults } from "@/components/inventory/reports/AdjustmentReportResults";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS, type PageSizeOption } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Package,
  Warehouse,
} from "lucide-react";
import type {
  InventoryItem,
  StockSummaryItem,
  StockTransaction,
} from "@/types/inventory";
import { ItemPickerSelect } from "@/components/inventory/shared/ItemPickerSelect";

const CURRENT_YEAR = new Date().getFullYear();
const ADJUSTMENT_YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

function InventoryReportsPage() {
  const {
    fetchStockSummary,
    fetchMovementReport,
    fetchItemHistory,
    fetchWarehouseReport,
    fetchAdjustmentReport,
    exportStockSummary,
    exportMovementReport,
    exportItemHistory,
    exportWarehouseReport,
    exportAdjustmentReport,
  } = useInventoryReports();
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  // Full record of the item picked for Item History -- the picker searches
  // the server (ItemPickerSelect), so there's no local catalog to look it up in.
  const [historyItem, setHistoryItem] = useState<InventoryItem | undefined>(
    undefined,
  );

  const [activeReport, setActiveReport] = useState<string | null>(null);

  const [movementParams, setMovementParams] = useState({
    start_date: "",
    end_date: "",
    warehouse: "all",
    item: "all",
  });
  const [itemHistoryParams, setItemHistoryParams] = useState({
    item: "all",
    start_date: "",
    end_date: "",
  });
  const [warehouseParams, setWarehouseParams] = useState({ warehouse: "all" });
  const [adjustmentParams, setAdjustmentParams] = useState({
    warehouse: "all",
    year: String(CURRENT_YEAR),
  });
  const [movementDateRange, setMovementDateRange] = useState<DateRange | undefined>();
  const [itemHistoryDateRange, setItemHistoryDateRange] = useState<DateRange | undefined>();
  // The date range above used to be collected and never sent, so the
  // report (and its export) always covered all time. Shared by the fetch,
  // paging and the export so they can't disagree (INV-25).
  const itemHistoryFilters = () => ({
    from_date: itemHistoryParams.start_date || undefined,
    to_date: itemHistoryParams.end_date || undefined,
  });
  const {
    page: itemHistoryPage,
    setPage: setItemHistoryPage,
    pageSize: itemHistoryPageSize,
    handlePageSizeChange: handleItemHistoryPageSizeChange,
  } = usePaginationState();

  const isLoading =
    fetchStockSummary.isPending ||
    fetchMovementReport.isPending ||
    fetchItemHistory.isPending ||
    fetchWarehouseReport.isPending ||
    fetchAdjustmentReport.isPending;

  const summaryRows: StockSummaryItem[] =
    (activeReport === "stock_summary"
      ? fetchStockSummary.data
      : activeReport === "warehouse"
        ? fetchWarehouseReport.data
        : undefined) ?? [];
  const historyRows: StockTransaction[] =
    (activeReport === "item_history"
      ? fetchItemHistory.data?.results
      : undefined) ?? [];
  const reportRowCount =
    activeReport === "item_history" ? historyRows.length : summaryRows.length;
  const itemHistoryCount = fetchItemHistory.data?.count ?? 0;
  const movementReportData =
    activeReport === "movement" ? (fetchMovementReport.data ?? null) : null;
  const adjustmentReportData =
    activeReport === "adjustment" ? (fetchAdjustmentReport.data ?? null) : null;

  // Reset to page 1 whenever the item history item filter changes
  useEffect(() => {
    setItemHistoryPage(1);
  }, [itemHistoryParams.item, setItemHistoryPage]);

  const runReport = async (type: string) => {
    setActiveReport(type);
    try {
      if (type === "stock_summary") {
        await fetchStockSummary.mutateAsync({});
      } else if (type === "movement") {
        const params: MovementReportParams = {};
        if (movementParams.start_date)
          params.from_date = movementParams.start_date;
        if (movementParams.end_date) params.to_date = movementParams.end_date;
        if (movementParams.warehouse !== "all")
          params.warehouse = Number(movementParams.warehouse);
        if (movementParams.item !== "all")
          params.item = Number(movementParams.item);
        await fetchMovementReport.mutateAsync(params);
      } else if (type === "item_history") {
        if (itemHistoryParams.item === "all") {
          toast.error("Select an item");
          return;
        }
        setItemHistoryPage(1);
        await fetchItemHistory.mutateAsync({
          itemId: Number(itemHistoryParams.item),
          page: 1,
          page_size: itemHistoryPageSize,
          ...itemHistoryFilters(),
        });
      } else if (type === "warehouse") {
        if (warehouseParams.warehouse === "all") {
          toast.error("Select a warehouse");
          return;
        }
        await fetchWarehouseReport.mutateAsync({
          warehouseId: Number(warehouseParams.warehouse),
        });
      } else if (type === "adjustment") {
        const params: AdjustmentReportParams = { year: Number(adjustmentParams.year) };
        if (adjustmentParams.warehouse !== "all")
          params.warehouse = Number(adjustmentParams.warehouse);
        await fetchAdjustmentReport.mutateAsync(params);
      }
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const isExporting =
    exportStockSummary.isPending ||
    exportMovementReport.isPending ||
    exportItemHistory.isPending ||
    exportWarehouseReport.isPending ||
    exportAdjustmentReport.isPending;

  // Re-requests the currently active report's exact params server-side as an
  // .xlsx download -- "the generated results", not a separate export config.
  const handleExport = () => {
    if (activeReport === "stock_summary") {
      exportStockSummary.mutate({});
    } else if (activeReport === "movement") {
      const params: MovementReportParams = {};
      if (movementParams.start_date) params.from_date = movementParams.start_date;
      if (movementParams.end_date) params.to_date = movementParams.end_date;
      if (movementParams.warehouse !== "all")
        params.warehouse = Number(movementParams.warehouse);
      if (movementParams.item !== "all") params.item = Number(movementParams.item);
      exportMovementReport.mutate(params);
    } else if (activeReport === "item_history" && itemHistoryParams.item !== "all") {
      const item =
        historyItem && String(historyItem.id) === itemHistoryParams.item
          ? historyItem
          : undefined;
      const filters = itemHistoryFilters();
      const range =
        filters.from_date || filters.to_date
          ? `_${filters.from_date ?? "start"}_to_${filters.to_date ?? "today"}`
          : "";
      exportItemHistory.mutate({
        itemId: Number(itemHistoryParams.item),
        ...filters,
        filename: item ? `item_history_${item.sku || item.name}${range}.xlsx` : undefined,
      });
    } else if (activeReport === "warehouse" && warehouseParams.warehouse !== "all") {
      const warehouse = warehouses.find(
        (w) => String(w.id) === warehouseParams.warehouse,
      );
      exportWarehouseReport.mutate({
        warehouseId: Number(warehouseParams.warehouse),
        filename: warehouse
          ? `warehouse_report_${warehouse.name}.xlsx`
          : undefined,
      });
    } else if (activeReport === "adjustment") {
      const params: AdjustmentReportParams = { year: Number(adjustmentParams.year) };
      if (adjustmentParams.warehouse !== "all")
        params.warehouse = Number(adjustmentParams.warehouse);
      exportAdjustmentReport.mutate(params);
    }
  };

  const itemHistoryTotalPages = useMemo(
    () =>
      itemHistoryCount ? Math.ceil(itemHistoryCount / itemHistoryPageSize) : 1,
    [itemHistoryCount, itemHistoryPageSize],
  );

  const handleItemHistoryPageChange = (newPage: number) => {
    if (newPage < 1 || newPage > itemHistoryTotalPages) return;
    setItemHistoryPage(newPage);
    fetchItemHistory.mutate({
      itemId: Number(itemHistoryParams.item),
      page: newPage,
      page_size: itemHistoryPageSize,
      ...itemHistoryFilters(),
    });
  };

  const handleItemHistoryPageSizeChangeAndRefetch = (size: PageSizeOption) => {
    handleItemHistoryPageSizeChange(size);
    fetchItemHistory.mutate({
      itemId: Number(itemHistoryParams.item),
      page: 1,
      page_size: size,
      ...itemHistoryFilters(),
    });
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-6 p-6 pt-0">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Inventory Reports
          </h1>
          <p className="text-muted-foreground">
            Generate stock and movement reports
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Stock Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4" />
                Stock Summary
              </CardTitle>
              <CardDescription>
                View current stock levels across all warehouses with low-stock
                flags.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col justify-end items-start h-full space-y-3">
              <Button
                size="sm"
                onClick={() => runReport("stock_summary")}
                disabled={isLoading}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Generate Report
              </Button>
            </CardContent>
          </Card>

          {/* Movement Report */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4" />
                Stock Movement
              </CardTitle>
              <CardDescription>
                View stock movements across all warehouses within a date range.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 items-center">
                <div className="flex flex-col justify-center">
                  <Label className="text-xs">Warehouse</Label>
                  <Select
                    value={movementParams.warehouse}
                    onValueChange={(v) =>
                      setMovementParams((p) => ({ ...p, warehouse: v }))
                    }
                  >
                    <SelectTrigger className="h-8 text-sm w-auto">
                      <SelectValue placeholder="All warehouses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Warehouses</SelectItem>
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={String(w.id)}>
                          {w.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col justify-center">
                  <Label className="text-xs">Date Range</Label>
                  <DateSelector
                    value={movementDateRange}
                    onApply={(range) => {
                      setMovementDateRange(range);
                      setMovementParams((p) => ({
                        ...p,
                        start_date: range?.from ? dayjs(range.from).format("YYYY-MM-DD") : "",
                        end_date: range?.to ? dayjs(range.to).format("YYYY-MM-DD") : "",
                      }));
                    }}
                    onClear={() => {
                      setMovementDateRange(undefined);
                      setMovementParams((p) => ({ ...p, start_date: "", end_date: "" }));
                    }}
                    label="Select date range"
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              <Button
                size="sm"
                onClick={() => runReport("movement")}
                disabled={isLoading}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Generate Report
              </Button>
            </CardContent>
          </Card>

          {/* Item History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-4 w-4" />
                Item History
              </CardTitle>
              <CardDescription>
                View stock movements for a specific item within a date range.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 items-center">
                <div className="flex flex-col justify-center">
                  <Label className="text-xs">Item</Label>
                  <ItemPickerSelect
                    // Inactive items included -- their history is still
                    // worth reporting on.
                    params={{ is_active: undefined }}
                    className="h-8 text-sm w-auto min-w-48"
                    selectedItem={historyItem}
                    watchedValue={
                      itemHistoryParams.item === "all"
                        ? null
                        : Number(itemHistoryParams.item)
                    }
                    onSelect={(v, item) => {
                      setHistoryItem(item);
                      setItemHistoryParams((p) => ({
                        ...p,
                        item: v == null ? "all" : String(v),
                      }));
                    }}
                    placeholder="Select item *"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <Label className="text-xs">Date Range</Label>
                  <DateSelector
                    value={itemHistoryDateRange}
                    onApply={(range) => {
                      setItemHistoryDateRange(range);
                      setItemHistoryParams((p) => ({
                        ...p,
                        start_date: range?.from ? dayjs(range.from).format("YYYY-MM-DD") : "",
                        end_date: range?.to ? dayjs(range.to).format("YYYY-MM-DD") : "",
                      }));
                    }}
                    onClear={() => {
                      setItemHistoryDateRange(undefined);
                      setItemHistoryParams((p) => ({ ...p, start_date: "", end_date: "" }));
                    }}
                    label="Select date range"
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => runReport("item_history")}
                disabled={isLoading}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Generate Report
              </Button>
            </CardContent>
          </Card>

          {/* Warehouse Report */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Warehouse className="h-4 w-4" />
                Warehouse Report
              </CardTitle>
              <CardDescription>
                View stock levels grouped by warehouse location.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col justify-center">
                <Label className="text-xs">Warehouse</Label>
                <Select
                  value={warehouseParams.warehouse}
                  onValueChange={(v) => setWarehouseParams({ warehouse: v })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Select warehouse...</SelectItem>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                onClick={() => runReport("warehouse")}
                disabled={isLoading}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Generate Report
              </Button>
            </CardContent>
          </Card>

          {/* Adjustment / Loss Report */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingDown className="h-4 w-4" />
                Adjustment &amp; Loss Report
              </CardTitle>
              <CardDescription>
                Stock gained or lost via Physical Count, Correction, and
                Opening Balance adjustments, broken down by month.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 items-center">
                <div className="flex flex-col justify-center">
                  <Label className="text-xs">Warehouse</Label>
                  <Select
                    value={adjustmentParams.warehouse}
                    onValueChange={(v) =>
                      setAdjustmentParams((p) => ({ ...p, warehouse: v }))
                    }
                  >
                    <SelectTrigger className="h-8 text-sm w-auto">
                      <SelectValue placeholder="All warehouses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Warehouses</SelectItem>
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={String(w.id)}>
                          {w.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col justify-center">
                  <Label className="text-xs">Year</Label>
                  <Select
                    value={adjustmentParams.year}
                    onValueChange={(v) =>
                      setAdjustmentParams((p) => ({ ...p, year: v }))
                    }
                  >
                    <SelectTrigger className="h-8 text-sm w-auto">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ADJUSTMENT_YEAR_OPTIONS.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                size="sm"
                onClick={() => runReport("adjustment")}
                disabled={isLoading}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                Generate Report
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Movement Report Output */}
        {activeReport === "movement" && movementReportData && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Stock Movement Summary
              </CardTitle>
              <CardAction>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={handleExport}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileDown className="h-3.5 w-3.5" />
                  )}
                  Export
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground">Total Inflow</p>
                  <p className="text-xl font-semibold text-green-600">
                    +{movementReportData.total_inflow}
                  </p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground">Total Outflow</p>
                  <p className="text-xl font-semibold text-red-600">
                    -{movementReportData.total_outflow}
                  </p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground">Net Change</p>
                  <p
                    className={`text-xl font-semibold ${movementReportData.net_change >= 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    {movementReportData.net_change >= 0 ? "+" : ""}
                    {movementReportData.net_change}
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto scrollbar-overlay">
                <div className="rounded-md border w-full">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary hover:bg-secondary">
                        <TableHead>Transaction Type</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">
                          Total Quantity
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(
                        movementReportData.transactions_by_type,
                      ).map(([type, entry]) => (
                        <TableRow key={type}>
                          <TableCell className="font-medium">
                            {type.replace(/_/g, " ")}
                          </TableCell>
                          <TableCell className="text-right">
                            {entry.count}
                          </TableCell>
                          <TableCell className="text-right">
                            {entry.total_quantity}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeReport === "movement" && !movementReportData && !isLoading && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No data found for the selected filters.
            </CardContent>
          </Card>
        )}

        {/* Adjustment Report Output */}
        {activeReport === "adjustment" && adjustmentReportData && (
          <AdjustmentReportResults
            data={adjustmentReportData}
            onExport={handleExport}
            isExporting={isExporting}
          />
        )}

        {activeReport === "adjustment" &&
          !adjustmentReportData &&
          !isLoading && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No data found for the selected filters.
              </CardContent>
            </Card>
          )}

        {/* Table-based Report Output */}
        {activeReport &&
          activeReport !== "movement" &&
          reportRowCount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Report Results (
                  {activeReport === "item_history"
                    ? itemHistoryCount
                    : reportRowCount}{" "}
                  rows)
                </CardTitle>
                <CardAction>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileDown className="h-3.5 w-3.5" />
                    )}
                    Export
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent>
                {(activeReport === "stock_summary" ||
                  activeReport === "warehouse") && (
                  <StockLevelTable
                    stockLevels={summaryRows.map((d) => ({
                      id: d.item_id,
                      item: d.item_id,
                      item_name: d.item_name,
                      item_sku: d.item_sku,
                      warehouse: d.warehouse_id,
                      warehouse_name: d.warehouse_name,
                      quantity: d.quantity,
                      available: d.quantity,
                      on_hand: d.quantity,
                      reserved: "0",
                      soft_reserved: "0",
                      in_transit: "0",
                      on_order: "0",
                      base_unit: d.base_unit,
                      minimum_level: d.minimum_level,
                      reorder_point: d.reorder_point,
                      updated_at: "",
                      variant_id: null,
                      variant_sku: null,
                      variant_label: null,
                      variant_barcode: null,
                      item_thumbnail_url: null,
                      thumbnail_url: null,
                    }))}
                    isLoading={isLoading}
                    onItemClick={() => {}}
                    onView={() => {}}
                    onEdit={() => {}}
                  />
                )}
                {activeReport === "item_history" && (
                  <>
                    <TransactionTable
                      transactions={historyRows}
                      isLoading={isLoading}
                    />
                    <PaginationComponent
                      count={itemHistoryCount}
                      page={itemHistoryPage}
                      onPageChange={handleItemHistoryPageChange}
                      isLoading={isLoading}
                      pageSize={itemHistoryPageSize}
                      onPageSizeChange={handleItemHistoryPageSizeChangeAndRefetch}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          )}

        {activeReport &&
          activeReport !== "movement" &&
          activeReport !== "adjustment" &&
          reportRowCount === 0 &&
          !isLoading && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No data found for the selected filters.
              </CardContent>
            </Card>
          )}
      </div>
    </SidebarInset>
  );
}
